import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../../components/ui/dialog";
import { BookOpen, Plus, Edit, Trash2 } from "lucide-react";
import { API } from "../../../App";
import PilotFlightForm, { PILOT_FLIGHT_TYPE_LABEL } from "./PilotFlightForm";
import { companionRoleLabel } from "./diUtils";

const fmtMin = (min) => {
  const m = Math.max(0, parseInt(min || 0, 10));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}:${String(mm).padStart(2, "0")}`;
};

export default function PilotFlightsDialog({ pilot, onClose }) {
  const [flights, setFlights] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const PAGE_SIZE = 50;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/di/pilots/${pilot.id}/flights?page=${p}&page_size=${PAGE_SIZE}`,
        { withCredentials: true }
      );
      setFlights(r.data.data || []);
      setPagination({ page: r.data.page || 1, total: r.data.total || 0, totalPages: r.data.total_pages || 1 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar voos");
    } finally { setLoading(false); }
  }, [pilot.id]);

  useEffect(() => { load(1); }, [load]);

  const remove = async (f) => {
    if (!window.confirm("Eliminar este voo?")) return;
    try {
      await axios.delete(`${API}/di/pilots/${pilot.id}/flights/${f.id}`, { withCredentials: true });
      load(pagination.page);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const totalMin = flights.reduce((acc, f) => acc + Number(f.pic_minutes || 0), 0);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="di-pilot-flights-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><BookOpen className="w-5 h-5" /> Voos · {pilot.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Registo de horas voadas por este piloto. Voos com aeronaves da escola contam para o total dessa aeronave.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between mb-2 text-xs">
          <div className="text-slate-400">
            {pagination.total} voo(s) · pág. {pagination.page}/{pagination.totalPages}
            {flights.length > 0 && <span> · total pág. <span className="font-mono text-emerald-300">{fmtMin(totalMin)}</span></span>}
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="di-pilot-flight-add-btn" className="btn-cockpit">
            <Plus className="w-3.5 h-3.5 mr-1" /> Novo voo
          </Button>
        </div>
        {loading ? (
          <div className="py-6 text-center text-slate-500 text-xs">A carregar…</div>
        ) : flights.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-xs border border-dashed border-cockpit-border rounded">Sem voos registados.</div>
        ) : (
          <div className="border border-cockpit-border rounded overflow-hidden">
            <table className="w-full text-[11px]" data-testid="di-pilot-flights-table">
              <thead className="bg-slate-800 text-slate-400">
                <tr><th className="text-left p-1.5">Data</th><th className="text-left p-1.5">Tipo</th><th className="text-left p-1.5">Matrícula</th><th className="text-right p-1.5">Tempo</th><th className="text-right p-1.5">DC</th><th className="text-left p-1.5">Notas</th><th className="text-right p-1.5"></th></tr>
              </thead>
              <tbody>
                {flights.map((f) => {
                  const isCompanion = f.role === "companion";
                  return (
                    <tr key={f.id} className={`border-t border-slate-800 hover:bg-slate-800/30 ${isCompanion ? "bg-emerald-500/[0.03]" : ""}`} data-testid={`di-pilot-flight-row-${f.id}`}>
                      <td className="p-1.5 font-mono">{f.flight_date}</td>
                      <td className="p-1.5">
                        {isCompanion ? <span className="inline-flex items-center gap-1"><span className="text-[9px] uppercase font-bold px-1 rounded bg-emerald-500/20 text-emerald-300 tracking-wide" data-testid="di-pilot-flight-companion-badge">{companionRoleLabel({ companion_kind: "pilot", flight_type: f.flight_type })}</span><span className="text-slate-400 text-[10px]">{PILOT_FLIGHT_TYPE_LABEL[f.flight_type] || f.flight_type}</span></span> : (PILOT_FLIGHT_TYPE_LABEL[f.flight_type] || f.flight_type)}
                      </td>
                      <td className="p-1.5 font-mono">{f.aircraft_registration || "—"}</td>
                      <td className="p-1.5 font-mono text-right">{fmtMin(f.pic_minutes)}</td>
                      <td className="p-1.5 font-mono text-right text-emerald-300">{f.dc_minutes > 0 ? fmtMin(f.dc_minutes) : "—"}</td>
                      <td className="p-1.5 text-slate-400 truncate max-w-[150px]">{f.notes || ""}</td>
                      <td className="p-1.5 text-right">
                        {isCompanion ? <span className="text-[9px] text-slate-500 italic" title="Voo de outro tripulante — edita no diário dele">só leitura</span> : (
                          <><button onClick={() => { setEditing(f); setShowForm(true); }} className="text-slate-400 hover:text-orange-300 mr-1.5"><Edit className="w-3 h-3 inline" /></button><button onClick={() => remove(f)} className="text-slate-400 hover:text-red-400"><Trash2 className="w-3 h-3 inline" /></button></>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between text-[10px] text-slate-400 border-t border-cockpit-border pt-2 mt-1 flex-wrap gap-2">
            <span>{pagination.total} voo(s) · página {pagination.page} de {pagination.totalPages}</span>
            <div className="flex gap-1">
              <button disabled={pagination.page <= 1} onClick={() => load(1)} className="px-1.5 py-0.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800">«</button>
              <button disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)} className="px-2 py-0.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800">‹</button>
              <button disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.page + 1)} className="px-2 py-0.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800">›</button>
              <button disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.totalPages)} className="px-1.5 py-0.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800">»</button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </DialogFooter>
        {showForm && <PilotFlightForm pilotId={pilot.id} flight={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(pagination.page); }} />}
      </DialogContent>
    </Dialog>
  );
}
